import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { InjectedFormProps } from 'redux-form'

import useListingsQuery from '~/hooks/requests/useListings'
import { isOfType } from '~/utils/functions'
import Paths from '~/utils/paths'

import { typeRedirects } from '~/brands'

export const parseSearchToParams = (path: any) => {
  const currentSearch = Paths.search.parseSearch(path)

  const {
    by_type,
    category,
    make,
    location,
    by_location,
    price,
    odometer,
    keyword,
    year,
    length,
    length_feet,
    boat_trailer_length,
    boat_trailer_length_feet,
    berths,
    tow_weight,
    atm,
    boat_draft,
    ...queries
  } = currentSearch

  let type = by_type
  const typeRedirect = typeRedirects.find((t) => t.old === type)
  if (typeRedirect) {
    type = typeRedirect.new
  }

  const sliderValuesIsValid = (values: string[]) =>
    values?.every((el) => isOfType.number(+el))

  return {
    formValues: {
      ...currentSearch,
      price: sliderValuesIsValid(price) ? price : undefined
    },
    params: {
      ...queries,
      by_type: type,
      by_category: category,
      by_make: make?.name,
      by_model: make?.model,
      by_series: make?.series,
      by_location: location || by_location,
      by_keyword: keyword,
      price_min: sliderValuesIsValid(price) ? price?.[0] : undefined,
      price_max: sliderValuesIsValid(price) ? price?.[1] : undefined,
      odometer_min: sliderValuesIsValid(odometer) ? odometer?.[0] : undefined,
      odometer_max: sliderValuesIsValid(odometer) ? odometer?.[1] : undefined,
      min_year: year?.min,
      max_year: year?.max,
      by_min_length: length?.min,
      by_max_length: length?.max,
      by_min_length_feet: length_feet?.min,
      by_max_length_feet: length_feet?.max,
      by_min_boat_trailer_length: boat_trailer_length?.min,
      by_max_boat_trailer_length: boat_trailer_length?.max,
      by_min_boat_trailer_length_feet: boat_trailer_length_feet?.min,
      by_max_boat_trailer_length_feet: boat_trailer_length_feet?.max,
      by_min_berths: berths?.min,
      by_max_berths: berths?.max,
      by_min_tow_weight: tow_weight?.min,
      by_max_tow_weight: tow_weight?.max,
      by_min_atm: atm?.min,
      by_max_atm: atm?.max,
      by_min_draft: boat_draft?.min,
      by_max_draft: boat_draft?.max
    },
    redirect: !!price && !sliderValuesIsValid(price)
  }
}

type Props = {
  change: InjectedFormProps['change']
  initialize?: InjectedFormProps['initialize']
  path: string
}

const useSearchHandler = ({ change, path }: Props) => {
  const router = useRouter()

  const searchParams = useMemo(() => parseSearchToParams(path), [path])

  const {
    data: { data: listings, pagy, meta } = {},
    isFetching,
    isFetched
  } = useListingsQuery({ key: path, ...searchParams.params })

  const handleChangePage = useCallback((_event, page): void => {
    change('page', page > 1 ? page : null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleReset = () => {
    router.push('/search', undefined, { shallow: true })
  }

  return {
    handleChangePage,
    handleReset,
    listings,
    meta,
    pagy,
    isFetching,
    isFetched,
    searchParams
  }
}

export default useSearchHandler
